import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { LogOut, User, FileText, Home, ChevronDown } from 'lucide-react';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setServicesOpen(false);
      }
    };

    // Add both mouse and touch event listeners for mobile compatibility
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setMobileMenuOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to={user ? "/dashboard" : "/"} className="flex items-center space-x-2">
            <div className="text-2xl font-bold text-blue-600">ITRQUICK</div>
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            {/* Public Menu Items */}
            <Link 
              to="/" 
              className={`transition font-medium ${isActive('/') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'}`}
              data-testid="nav-home"
            >
              Home
            </Link>
            
            <Link 
              to="/about" 
              className={`transition font-medium ${isActive('/about') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'}`}
              data-testid="nav-about"
            >
              About Us
            </Link>

            {/* Services Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setServicesOpen(!servicesOpen);
                }}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  setServicesOpen(!servicesOpen);
                }}
                className={`flex items-center space-x-1 transition font-medium ${
                  isActive('/itr-services') || isActive('/gst-services') 
                    ? 'text-blue-600' 
                    : 'text-gray-700 hover:text-blue-600'
                }`}
                data-testid="nav-services-dropdown"
              >
                <span>Our Services</span>
                <ChevronDown size={16} className={`transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
              </button>

              {servicesOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2" data-testid="services-dropdown-menu">
                  <Link
                    to="/itr-services"
                    className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
                    onClick={() => setServicesOpen(false)}
                    data-testid="nav-itr-services"
                  >
                    ITR Filing
                  </Link>
                  <Link
                    to="/gst-services"
                    className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
                    onClick={() => setServicesOpen(false)}
                    data-testid="nav-gst-services"
                  >
                    GST Services
                  </Link>
                  <div className="border-t border-gray-200 my-2"></div>
                  <Link
                    to="/services"
                    className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition font-medium"
                    onClick={() => setServicesOpen(false)}
                    data-testid="nav-all-services"
                  >
                    View All Services
                  </Link>
                </div>
              )}
            </div>

            <Link 
              to="/contact" 
              className={`transition font-medium ${isActive('/contact') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'}`}
              data-testid="nav-contact"
            >
              Contact Us
            </Link>

            {/* User Menu */}
            {user ? (
              <>
                <div className="border-l border-gray-300 h-6"></div>
                <Link 
                  to="/dashboard" 
                  className={`flex items-center space-x-1 transition font-medium px-3 py-2 rounded-lg ${isActive('/dashboard') ? 'bg-green-600 text-white' : 'bg-green-100 text-green-700 hover:bg-green-600 hover:text-white'}`} 
                  data-testid="nav-dashboard"
                  title="Your main dashboard"
                >
                  <Home size={18} />
                  <span>Dashboard</span>
                </Link>
                {user.role === 'admin' && (
                  <Link 
                    to="/admin" 
                    className={`transition font-medium px-3 py-2 rounded-lg ${isActive('/admin') ? 'bg-orange-600 text-white' : 'bg-orange-100 text-orange-700 hover:bg-orange-600 hover:text-white'}`} 
                    data-testid="nav-admin"
                    title="Admin Dashboard"
                  >
                    Admin
                  </Link>
                )}
                {user.role === 'manager' && (
                  <Link 
                    to="/manager" 
                    className={`transition font-medium px-3 py-2 rounded-lg ${isActive('/manager') ? 'bg-orange-600 text-white' : 'bg-orange-100 text-orange-700 hover:bg-orange-600 hover:text-white'}`} 
                    data-testid="nav-manager"
                    title="Manager Dashboard"
                  >
                    Manager
                  </Link>
                )}
                <Link 
                  to="/tax-locker" 
                  className={`transition font-medium px-3 py-2 rounded-lg ${isActive('/tax-locker') ? 'bg-red-600 text-white' : 'bg-red-100 text-red-700 hover:bg-red-600 hover:text-white'}`} 
                  data-testid="nav-tax-locker"
                  title="All your files uploaded can be seen here"
                >
                  Tax Locker
                </Link>
                <Link to="/profile" className={`flex items-center space-x-2 transition font-medium ${isActive('/profile') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'}`} data-testid="nav-profile">
                  <User size={18} />
                  <span>{user.name}</span>
                </Link>
                <button onClick={handleLogout} className="flex items-center space-x-1 text-red-600 hover:text-red-700 transition font-medium" data-testid="nav-logout">
                  <LogOut size={18} />
                  <span>Logout</span>
                </button>
              </>
            ) : (
              <>
                <div className="border-l border-gray-300 h-6"></div>
                <Link to="/login" className="text-gray-700 hover:text-blue-600 transition font-medium" data-testid="nav-login">Login</Link>
                <Link to="/register" className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition font-medium" data-testid="nav-register">Get Started</Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
            className="md:hidden text-gray-700 p-2 hover:bg-gray-100 rounded-lg transition"
            aria-label="Toggle mobile menu"
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4 space-y-2">
            <Link 
              to="/" 
              className={`block px-4 py-2 rounded-lg transition ${isActive('/') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100'}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>
            <Link 
              to="/about" 
              className={`block px-4 py-2 rounded-lg transition ${isActive('/about') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100'}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              About Us
            </Link>
            
            {/* Mobile Services Submenu */}
            <div className="px-4 py-2">
              <button
                onClick={() => setServicesOpen(!servicesOpen)}
                className="flex items-center justify-between w-full text-left text-gray-700 hover:text-blue-600"
              >
                <span>Our Services</span>
                <ChevronDown size={16} className={`transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {servicesOpen && (
                <div className="ml-4 mt-2 space-y-2">
                  <Link
                    to="/services/itr-filing"
                    className="block py-2 text-gray-600 hover:text-blue-600"
                    onClick={() => { setServicesOpen(false); setMobileMenuOpen(false); }}
                  >
                    ITR Filing
                  </Link>
                  <Link
                    to="/services/gst"
                    className="block py-2 text-gray-600 hover:text-blue-600"
                    onClick={() => { setServicesOpen(false); setMobileMenuOpen(false); }}
                  >
                    GST Services
                  </Link>
                  <Link
                    to="/services"
                    className="block py-2 text-gray-600 hover:text-blue-600"
                    onClick={() => { setServicesOpen(false); setMobileMenuOpen(false); }}
                  >
                    View All Services
                  </Link>
                </div>
              )}
            </div>

            <Link 
              to="/contact" 
              className={`block px-4 py-2 rounded-lg transition ${isActive('/contact') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100'}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Contact Us
            </Link>

            <div className="border-t border-gray-200 my-2"></div>

            {/* Mobile User Menu */}
            {user ? (
              <>
                <Link 
                  to="/dashboard" 
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition ${isActive('/dashboard') ? 'bg-green-600 text-white' : 'bg-green-100 text-green-700'}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Home size={18} />
                  <span>Dashboard</span>
                </Link>
                {user.role === 'admin' && (
                  <Link 
                    to="/admin" 
                    className={`block px-4 py-2 rounded-lg transition ${isActive('/admin') ? 'bg-orange-600 text-white' : 'bg-orange-100 text-orange-700'}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Admin
                  </Link>
                )}
                {user.role === 'manager' && (
                  <Link 
                    to="/manager" 
                    className={`block px-4 py-2 rounded-lg transition ${isActive('/manager') ? 'bg-orange-600 text-white' : 'bg-orange-100 text-orange-700'}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Manager
                  </Link>
                )}
                <Link 
                  to="/tax-locker" 
                  className={`block px-4 py-2 rounded-lg transition ${isActive('/tax-locker') ? 'bg-red-600 text-white' : 'bg-red-100 text-red-700'}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Tax Locker
                </Link>
                <Link 
                  to="/profile" 
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition ${isActive('/profile') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100'}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <User size={18} />
                  <span>{user.name}</span>
                </Link>
                <button 
                  onClick={handleLogout} 
                  className="flex items-center space-x-2 w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 rounded-lg transition"
                >
                  <LogOut size={18} />
                  <span>Logout</span>
                </button>
              </>
            ) : (
              <>
                <Link 
                  to="/login" 
                  className="block px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-100 transition"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Login
                </Link>
                <Link 
                  to="/register" 
                  className="block mx-4 px-6 py-2 text-center bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;